import { CardReadOperationRpcs } from "@ascleo/frontdesk-contracts/cardreader/rpc";
import { BookingQuestionnaireRpcs } from "@ascleo/frontdesk-contracts/booking-questionnaire/rpc";
import { FrontDeskSchedulingRpcs } from "@ascleo/frontdesk-contracts/scheduling/rpc";
/**
 * FrontDesk RPC Definitions
 *
 * RPC definitions for the FrontDesk kiosk application.
 * These provide high-level, workflow-aware operations.
 */

import { Rpc, RpcGroup } from "effect/rpc";
import { Schema } from "effect";
import {
  CardDataUnavailableErrorSchema,
  ExternalAPIErrorSchema,
  NotFoundErrorSchema,
  TerminalNotFoundErrorSchema,
} from "../errors";
import { ConnectionStatusSchema } from "../schemas/frontdesk/connectivity/connection-status.schema";
import { DeviceChecklistInputs } from "../services/frontdesk/device-checklist";
import { GetCardsFromTerminalRequestSchema } from "../schemas/frontdesk/cardreader/get-cards/get-cards.request";
import { GetCardsFromTerminalResponseSchema } from "../schemas/frontdesk/cardreader/get-cards/get-cards.response";
import {
  CardReadWorkflowResult,
  CardActionWorkflowResult,
} from "../schemas/frontdesk/cardreader/card-read-workflow-result.schema";
import { PatientSearchRequestSchema } from "../schemas/frontdesk/patient-search/patient-search.request";
import { PatientSearchResponseSchema } from "../schemas/frontdesk/patient-search/patient-search.response";
import { CheckInRequestSchema } from "../schemas/frontdesk/check-in/check-in.request";
import { CheckInResponseSchema } from "../schemas/frontdesk/check-in/check-in.response";
import { GetKimAccountRequestSchema } from "../schemas/frontdesk/kim-account/kim-account.request";
import { GetKimAccountResponseSchema } from "../schemas/frontdesk/kim-account/kim-account.response";
import { SearchInsurersRequestSchema } from "../schemas/frontdesk/insurance-search/insurance-search.request";
import { SearchInsurersResponseSchema } from "../schemas/frontdesk/insurance-search/insurance-search.response";
import { GetInsurerByIkRequestSchema } from "../schemas/frontdesk/insurance-search/get-insurer-by-ik.request";
import { GetInsurerByIkResponseSchema } from "../schemas/frontdesk/insurance-search/get-insurer-by-ik.response";
import { CheckQuarterlyCoverageRequestSchema } from "../schemas/frontdesk/quarterly-coverage/quarterly-coverage.request";
import { CheckQuarterlyCoverageResponseSchema } from "../schemas/frontdesk/quarterly-coverage/quarterly-coverage.response";
import { ListPrescriptionsRequestSchema } from "../schemas/frontdesk/prescription/list-prescriptions/list-prescriptions.request";
import { ListPrescriptionsResponseSchema } from "../schemas/frontdesk/prescription/list-prescriptions/list-prescriptions.response";
import { GetPrescriptionDetailRequestSchema } from "../schemas/frontdesk/prescription/get-prescription-detail/get-prescription-detail.request";
import { PrescriptionDetailSchema } from "../schemas/frontdesk/prescription/get-prescription-detail/get-prescription-detail.response";
import { MedicationHistoryRequestSchema } from "../schemas/frontdesk/prescription/medication-history/medication-history.request";
import { MedicationHistoryResponseSchema } from "../schemas/frontdesk/prescription/medication-history/medication-history.response";

// Request schemas
const ConnectionStatusRequestSchema = Schema.Struct({
  terminalId: Schema.String,
});

const DeviceChecklistRequestSchema = Schema.Struct({
  deviceIdHash: Schema.String,
  terminalId: Schema.String,
});

const ReadCardRequestSchema = Schema.Struct({
  terminalId: Schema.String,
});

const ApplyCardDataRequestSchema = Schema.Struct({
  crlogId: Schema.String,
});

const CancelCardReadRequestSchema = Schema.Struct({
  crlogId: Schema.String,
});

const SelectPatientRequestSchema = Schema.Struct({
  crlogId: Schema.String,
  patientId: Schema.String,
});

// Transformation error schema for RPC
const TransformationErrorSchema = Schema.TaggedStruct("TransformationError", {
  message: Schema.String,
});

/**
 * FrontDesk RPC Group
 *
 * High-level operations for the FrontDesk kiosk application:
 * - Connectivity: Check system status
 * - Card Reader: List cards, read cards with workflow
 * - Scheduling: Get appointment types and time slots
 */
export class FrontDeskRpcs extends RpcGroup.make(
  // Connectivity
  Rpc.make("FrontDeskConnectionStatus", {
    success: ConnectionStatusSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: ConnectionStatusRequestSchema,
  }),

  // Card Reader - Basic operations
  Rpc.make("FrontDeskGetCards", {
    success: GetCardsFromTerminalResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, TerminalNotFoundErrorSchema]),
    payload: GetCardsFromTerminalRequestSchema,
  }),

  // Card Reader - Workflow operations
  Rpc.make("FrontDeskReadCard", {
    success: CardReadWorkflowResult,
    error: Schema.Union(
      [CardDataUnavailableErrorSchema,
      ExternalAPIErrorSchema,
      NotFoundErrorSchema,
      TerminalNotFoundErrorSchema,
      TransformationErrorSchema],
    ),
    payload: ReadCardRequestSchema,
  }),

  Rpc.make("FrontDeskApplyCardData", {
    success: CardActionWorkflowResult,
    error: Schema.Union([ExternalAPIErrorSchema, TransformationErrorSchema]),
    payload: ApplyCardDataRequestSchema,
  }),

  Rpc.make("FrontDeskCancelCardRead", {
    success: CardActionWorkflowResult,
    error: Schema.Union([ExternalAPIErrorSchema, TransformationErrorSchema]),
    payload: CancelCardReadRequestSchema,
  }),

  Rpc.make("FrontDeskSelectPatient", {
    success: CardActionWorkflowResult,
    error: Schema.Union([ExternalAPIErrorSchema, TransformationErrorSchema]),
    payload: SelectPatientRequestSchema,
  }),

  // Patient Search - Privacy-safe patient identification
  Rpc.make("FrontDeskSearchPatient", {
    success: PatientSearchResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: PatientSearchRequestSchema,
  }),

  // Check-In - Patient check-in to appointments
  Rpc.make("FrontDeskCheckIn", {
    success: CheckInResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: CheckInRequestSchema,
  }),

  // KIM Account - Get practice KIM info for eEB
  Rpc.make("FrontDeskGetKimAccount", {
    success: GetKimAccountResponseSchema,
    error: ExternalAPIErrorSchema,
    payload: GetKimAccountRequestSchema,
  }),

  // Insurance Search - Search statutory (GKV) insurers
  Rpc.make("FrontDeskSearchInsurers", {
    success: SearchInsurersResponseSchema,
    error: ExternalAPIErrorSchema,
    payload: SearchInsurersRequestSchema,
  }),

  // Insurance Lookup - Get insurer by IK number
  Rpc.make("FrontDeskGetInsurerByIk", {
    success: GetInsurerByIkResponseSchema,
    error: ExternalAPIErrorSchema,
    payload: GetInsurerByIkRequestSchema,
  }),

  // Quarterly Coverage Check - Check if patient has current quarter coverage
  Rpc.make("FrontDeskCheckQuarterlyCoverage", {
    success: CheckQuarterlyCoverageResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: CheckQuarterlyCoverageRequestSchema,
  }),

  // Prescription - List active prescriptions for a patient
  Rpc.make("FrontDeskListPrescriptions", {
    success: ListPrescriptionsResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: ListPrescriptionsRequestSchema,
  }),

  // Prescription - Get prescription detail with medications
  Rpc.make("FrontDeskGetPrescriptionDetail", {
    success: PrescriptionDetailSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: GetPrescriptionDetailRequestSchema,
  }),

  // Prescription - Get medication history for selection UI
  Rpc.make("FrontDeskGetMedicationHistory", {
    success: MedicationHistoryResponseSchema,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: MedicationHistoryRequestSchema,
  }),

  // Onboarding - Per-device preflight checklist inputs (client derives positions)
  Rpc.make("FrontDeskGetDeviceChecklist", {
    success: DeviceChecklistInputs,
    error: Schema.Union([ExternalAPIErrorSchema, NotFoundErrorSchema]),
    payload: DeviceChecklistRequestSchema,
  }),
).merge(FrontDeskSchedulingRpcs).merge(BookingQuestionnaireRpcs).merge(CardReadOperationRpcs) {}
